import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, Info } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { menuItems, MenuItem } from './menuItems';
import WeatherGreeting from './WeatherGreeting';
import { ChangelogModal } from '../ChangelogModal';
import { useChangelog } from '../../hooks/useChangelog';

interface SidebarProps {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
}

export default function Sidebar({ isSidebarOpen, toggleSidebar }: SidebarProps) {
  const { isInSupportMode } = useAuth();
  const location = useLocation();
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [showChangelog, setShowChangelog] = useState(false);
  const { currentUser } = useAuth();
  const { currentVersion } = useChangelog();
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [hasBaselinkerProducts, setHasBaselinkerProducts] = useState<boolean | null>(null);

  useEffect(() => {
    const loadUserRoles = async () => {
      if (currentUser?.role) {
        const roleDoc = await getDoc(doc(db, 'roles', currentUser.role));
        if (roleDoc.exists()) {
          setUserRoles(roleDoc.data().menuAccess || []);
        }
      }
    };
    loadUserRoles();
  }, [currentUser]);

  // Verificar se o usuário tem produtos com baselinker_id
  useEffect(() => {
    const checkBaselinkerProducts = async () => {
      if (!currentUser) {
        setHasBaselinkerProducts(false);
        return;
      }

      try {
        // Query para produtos do usuário que têm baselinker_id
        const productsRef = collection(db, 'products');
        const q = query(
          productsRef,
          where('companyId', '==', currentUser.uid)
        );

        const querySnapshot = await getDocs(q);
        const hasProducts = querySnapshot.docs.some(doc => {
          const data = doc.data();
          return data.baselinker_id && data.baselinker_id.trim() !== '';
        });

        console.log('Verificando produtos com baselinker_id para:', currentUser.uid);
        console.log('Total de produtos:', querySnapshot.size);
        console.log('Tem produtos com baselinker_id:', hasProducts);

        setHasBaselinkerProducts(hasProducts);
      } catch (error) {
        console.error('Erro ao verificar produtos:', error);
        setHasBaselinkerProducts(false);
      }
    };

    checkBaselinkerProducts();
  }, [currentUser]);

  const handleItemClick = (item: MenuItem) => {
    if (item.subItems) {
      setExpandedItemId(expandedItemId === item.id ? null : item.id);
    } else if (item.path) {
      if (window.innerWidth < 1024) {
        toggleSidebar();
      }
    }
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const hasAccessToMenuItem = (item: MenuItem) => {
    // Se for o menu de Fulfillment, só mostra se tiver produtos com baselinker_id
    if (item.id === 'fulfillment' && !hasBaselinkerProducts) {
      return false;
    }

    if (currentUser?.role === 'admin') return true;
    
    // Verifica se tem acesso pelo ID do item
    if (userRoles.includes(item.id)) return true;
    
    // Verifica se tem acesso pelo path do item
    if (item.path && userRoles.includes(item.path)) return true;
    
    return false;
  };

  const filteredMenuItems = menuItems.map(item => {
    // Create a copy of the item
    const newItem = { ...item };
    
    // Check if user has access to this menu item
    if (!hasAccessToMenuItem(newItem)) {
      return null;
    }
    
    // If item has subitems, filter them
    if (newItem.subItems) {
      newItem.subItems = newItem.subItems.filter(subItem => 
        hasAccessToMenuItem(subItem)
      );
      // If no subitems remain after filtering, don't show parent item
      if (newItem.subItems.length === 0) {
        return null;
      }
    }
    
    return newItem;
  }).filter(Boolean) as MenuItem[];

  return (
    <div className="relative">
      <aside
        className={`fixed left-0 z-40 w-64 h-screen transition-all duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} bg-[rgb(10,25,47)] text-white shadow-xl flex flex-col font-['Exo_2'] ${isInSupportMode ? 'top-10' : 'top-0'}`}
      >
        <div className="flex flex-col h-full">
          <div className="p-2 flex justify-center border-b border-[rgba(255,255,255,0.1)]">
            <Link to="/" className="cursor-pointer">
              <img
                src="/lognow_branco.svg"
                alt="Logo"
                className="h-8 w-auto"
              />
            </Link>
          </div>
          
          <WeatherGreeting />

          <nav className="flex-1 px-2 py-2 space-y-1">
            {filteredMenuItems.map((item) => (
              <div key={item.label}>
                {/* Se tem submenus, sempre usa o botão */}
                {item.subItems ? (
                  <button
                    onClick={() => handleItemClick(item)}
                    className={`flex items-center justify-between w-full px-2 py-1.5 rounded-lg transition-colors text-sm ${
                      item.subItems?.some(sub => isActive(sub.path || ''))
                        ? 'bg-[rgba(255,255,255,0.1)]'
                        : 'hover:bg-[rgba(255,255,255,0.1)]'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <item.icon size={16} className="text-white" />
                      <span className="text-white text-sm">{item.label}</span>
                    </div>
                    <ChevronLeft
                      className={`transform transition-transform duration-200 text-white ${
                        expandedItemId === item.id ? '-rotate-90' : 'rotate-90'
                      }`}
                      size={14}
                    />
                  </button>
                ) : (
                  <Link
                    to={item.path || ''}
                    onClick={() => handleItemClick(item)}
                    className={`flex items-center justify-between w-full px-2 py-1.5 rounded-lg transition-colors text-sm ${
                      isActive(item.path || '')
                        ? 'bg-[rgba(255,255,255,0.1)]'
                        : 'hover:bg-[rgba(255,255,255,0.1)]'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <item.icon size={16} className="text-white" />
                      <span className="text-white text-sm">{item.label}</span>
                    </div>
                  </Link>
                )}
                
                {item.subItems && expandedItemId === item.id && (
                  <div className="ml-3 mt-1 space-y-0.5">
                    {item.subItems.map((subItem) => (
                      <Link
                        key={subItem.label}
                        to={subItem.path || ''}
                        onClick={() => handleItemClick(subItem)}
                        className={`flex items-center space-x-2 w-full px-2 py-1 rounded-lg transition-colors text-xs ${
                          isActive(subItem.path || '')
                            ? 'bg-[rgba(255,255,255,0.1)]'
                            : 'hover:bg-[rgba(255,255,255,0.1)]'
                        }`}
                      >
                        <subItem.icon size={14} className="text-white" />
                        <span className="text-white">{subItem.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Version Display */}
          <div className="p-4 border-t border-[rgba(255,255,255,0.1)]">
            <button
              onClick={() => setShowChangelog(true)}
              className="flex items-center justify-between w-full text-sm text-white/70 hover:text-white transition-colors"
            >
              <span>Versão {currentVersion}</span>
              <Info size={16} />
            </button>
          </div>
        </div>
      </aside>

      <button
        onClick={toggleSidebar}
        className={`fixed top-1/2 -translate-y-1/2 z-50 p-2 rounded-lg bg-[rgb(10,25,47)] text-white hover:bg-[rgba(255,255,255,0.1)] transition-all duration-300 ${
          isSidebarOpen ? 'left-64 -ml-3' : 'left-0'
        }`}
      >
        <ChevronLeft
          size={20}
          className={`transform transition-transform duration-300 ${
            !isSidebarOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Changelog Modal */}
      <ChangelogModal
        isOpen={showChangelog}
        onClose={() => setShowChangelog(false)}
      />
    </div>
  );
}